import { createContext, useEffect, useRef, useState } from "react";
import { buildScrollStorageKey } from "./useTableScrollRestore";
import { setProductTimeout, clearProductTimeout } from "@/utils/productTimers";

/**
 * The row id to light up, published by TablePanel and read by BodyRow. A
 * context rather than a prop because the value would otherwise be threaded
 * through three layers of grid plumbing (orchestrator, grid, grouped and
 * ungrouped row runs) that have no other reason to know about it.
 */
export const TableReturnHighlightContext = createContext<string | null>(null);

/**
 * The id of the list row currently being rendered, published by BodyRow.
 *
 * A row can send the user to a record from more than its Name column: the
 * aggregation views carry related-entity chips in ordinary fact cells, and
 * those already navigated before #9746. Those chips are `InlineEntityCard`s
 * rendered all over the app with no idea what contains them, so the row
 * identifies itself here instead. Null outside a list row, which is exactly
 * how a chip on an entity page or in the preview pane knows to record nothing.
 */
export const TableRowIdContext = createContext<string | null>(null);

/**
 * Temporarily highlights the row a user left from when they return to an RMS
 * list (#9746).
 *
 * Entity clicks inside RMS navigate rather than preview, so returning has to
 * be good enough that navigating away costs nothing. Scroll offset already
 * restores (#10594); this is the other half — the row is lit briefly so the
 * user can re-find their place, then fades on its own.
 */

export const HIGHLIGHT_DURATION_MS = 2500;

// Keyed off the same normalized list URL as the scroll offset, so a highlight
// and the offset it belongs with can never be scoped differently. Notably that
// strips ?entityId=/?docId=, which the preview writes via replaceState outside
// react-router and which the click-time URL therefore never had.
export const buildReturnRowStorageKey = (scrollStorageKey: string): string =>
  `rms-table-return-row:${scrollStorageKey}`;

/**
 * Record the row being navigated away from. Called at the click, before the
 * route changes.
 */
export function rememberReturnRow(
  pathname: string,
  search: string,
  rowId: string
): void {
  try {
    sessionStorage.setItem(
      buildReturnRowStorageKey(buildScrollStorageKey(pathname, search)),
      rowId
    );
  } catch {
    // Best-effort: losing the highlight is not worth breaking the navigation.
  }
}

export interface TableReturnHighlightOptions {
  /** The list's scroll-restore key; see `buildScrollStorageKey`. */
  storageKey: string;
  /** True once real rows are rendered — there is nothing to highlight before. */
  ready: boolean;
}

export interface TableReturnHighlightHandle {
  highlightedRowId: string | null;
}

export function useTableReturnHighlight({
  storageKey,
  ready,
}: TableReturnHighlightOptions): TableReturnHighlightHandle {
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  // "Temporarily" means once. Reading is destructive so a later visit the user
  // made for an unrelated reason doesn't re-light a stale row.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (!ready || consumedRef.current) {
      return;
    }
    consumedRef.current = true;

    const key = buildReturnRowStorageKey(storageKey);
    let rowId: string | null = null;
    try {
      rowId = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
    } catch {
      return;
    }
    if (!rowId) {
      return;
    }

    setHighlightedRowId(rowId);
    const timer = setProductTimeout(
      () => setHighlightedRowId(null),
      HIGHLIGHT_DURATION_MS
    );
    return () => clearProductTimeout(timer);
  }, [ready, storageKey]);

  return { highlightedRowId };
}
